import {
  CaretSortIcon,
  ExternalLinkIcon,
  LapTimerIcon,
  StackIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Popover,
  Text,
} from '@radix-ui/themes';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { CommonTooltip } from 'components/common/tooltip';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/common';
import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { MachineSubscriptionButton } from 'components/sections/admin-portal/machines/machine-subscription-button';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import env from 'config';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  getMachineStatusColor,
  getMachineStatusDisplay,
} from 'enums/admin-portal';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { t } from 'i18next';
import {
  LOCAL_DATETIME_FORMAT_LONG,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IError } from 'lib_ts/interfaces/common/i-error';
import { IErrorType } from 'lib_ts/interfaces/common/i-error-type';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { IDaemon } from 'lib_ts/interfaces/i-machine-daemons';
import { IWSMsg } from 'lib_ts/interfaces/i-machine-msg';
import { IMachineNetworkLog } from 'lib_ts/interfaces/i-machine-network-logs';
import { IReadyMsg } from 'lib_ts/interfaces/machine-msg/i-ready';
import { compact } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { paths } from 'routes/paths';
import { AdminMachinesService } from 'services/admin/machines.service';

const fahrenheitToCelsius = (f: number) => ((f - 32) * 5) / 9;

// Minimum acceptable throughput (Mbps), mirroring ArcMachine's system_monitoring
// thresholds; vision machines require more bandwidth.
const NETWORK_SPEED_MIN_MBPS = {
  vision: { download: 50, upload: 50 },
  nonVision: { download: 15, upload: 10 },
};

// Latency/packet-loss bounds, mirroring ArcMachine's net_latency_loss_check.
const MAX_LATENCY_MS_OPTIMAL = 50;
const MAX_LATENCY_MS_DEGRADED = 100;
const MAX_LOSS_PCT_DEGRADED = 1;

// green >= 1.5x min, yellow >= min, red below min
const speedColor = (mbps: number, min: number) => {
  if (mbps < min) {
    return RADIX.COLOR.DANGER;
  }

  if (mbps < min * 1.5) {
    return RADIX.COLOR.WARNING;
  }

  return RADIX.COLOR.SUCCESS;
};

// worst (most severe) of two speed colors, so the combined badge reflects the
// lower-performing direction relative to its own minimum
const worstSpeedColor = (
  a: ReturnType<typeof speedColor>,
  b: ReturnType<typeof speedColor>
) => {
  if (a === RADIX.COLOR.DANGER || b === RADIX.COLOR.DANGER) {
    return RADIX.COLOR.DANGER;
  }

  if (a === RADIX.COLOR.WARNING || b === RADIX.COLOR.WARNING) {
    return RADIX.COLOR.WARNING;
  }

  return RADIX.COLOR.SUCCESS;
};

const latencyColor = (ms: number) => {
  if (ms > MAX_LATENCY_MS_DEGRADED) {
    return RADIX.COLOR.DANGER;
  }

  if (ms > MAX_LATENCY_MS_OPTIMAL) {
    return RADIX.COLOR.WARNING;
  }

  return RADIX.COLOR.SUCCESS;
};

const packetLossColor = (pct: number) => {
  if (pct > MAX_LOSS_PCT_DEGRADED) {
    return RADIX.COLOR.DANGER;
  }

  if (pct > 0) {
    return RADIX.COLOR.WARNING;
  }

  return RADIX.COLOR.SUCCESS;
};

// TODO: websocket refactoring caused a regression here where this section is no longer aware if the machine has gone offline

const DaemonPopoverContent = (props: {
  error: IError;
  type: IErrorType | undefined;
}) => {
  const { buildParams } = useMachineSettingsDialogState();

  const heading = useMemo(() => {
    if (!props.type) {
      // shouldn't trigger
      return `${props.error.sender}-${props.error._id}`;
    }

    return `${props.error.sender}-${props.type.errorID}`;
  }, [props.error, props.type]);

  return (
    <ErrorBoundary identifier="DaemonPopoverContent">
      <Card size="3" className="no-background">
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            <Heading size="4" title={heading} truncate>
              {heading}
            </Heading>

            {props.type?.department && (
              <Badge color={RADIX.COLOR.NEUTRAL}>
                {props.type.department} Error
              </Badge>
            )}

            <Badge color={RADIX.COLOR.NEUTRAL}>
              {format(
                parseISO(props.error._created),
                LOCAL_DATETIME_FORMAT_LONG,
                {
                  timeZone: LOCAL_TIMEZONE,
                }
              )}
            </Badge>

            <Box flexGrow="1">{/* placeholder */}</Box>

            <Button
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              size="1"
              onClick={() => {
                // in a new tab, go to errors section > loads errors > opens the specific one by _id (if possible)
                const params = buildParams(
                  {
                    [MachineSettingsQueryParams.SectionID]:
                      MachineSettingsSection.Errors,
                    [MachineSettingsQueryParams.ErrorSender]:
                      props.error.sender,
                    [MachineSettingsQueryParams.ErrorID]:
                      props.error._id ?? undefined,
                  },
                  'error badge'
                );

                const url = `${env.self_url}${paths.admin.machines}?${params.toString()}`;
                window.open(url, '_blank');
              }}
            >
              <ExternalLinkIcon />
              Go to Error
            </Button>
          </Flex>
          <Card>
            {props.type ? (
              <Flex direction="column" gap="1">
                <Text color={RADIX.COLOR.SECONDARY}>CS Message</Text>

                <Box>
                  <ReactMarkdown children={props.type.cs_message || 'N/A'} />
                </Box>
              </Flex>
            ) : (
              <CommonCallout text="No error type definition was found for this error." />
            )}
          </Card>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};

const DaemonBadge = (props: { daemon: IDaemon }) => {
  const { daemon } = props;

  const [data, setData] = useState<{
    error: IError;
    type: IErrorType | undefined;
  }>();

  // load the actual error and its type definition
  useEffect(() => {
    if (!props.daemon.error_id) {
      setData(undefined);
      return;
    }

    AdminMachinesService.getInstance()
      .getMachineErrorWithType(props.daemon.error_id)
      .then((result) => setData(result));
  }, [props.daemon.error_id]);

  // the badge should still be able to render even if there is no error_id
  const badge = useMemo(() => {
    const label = [daemon.sender_id, data?.type?.department]
      .filter((m) => !!m)
      .join(HINT_DELIMITER);

    return (
      <Badge
        color={getMachineStatusColor(daemon.daemon_status)}
        className="cursor-pointer"
      >
        {label}
      </Badge>
    );
  }, [daemon, data?.type?.department]);

  if (!data?.type) {
    return badge;
  }

  // once the error and type have loaded, we can render w/ tooltip
  return (
    <ErrorBoundary identifier="DaemonBadge">
      <Popover.Root>
        <Popover.Trigger>{badge}</Popover.Trigger>
        <Popover.Content
          minWidth="400px"
          maxWidth="800px"
          style={{ padding: 0 }}
        >
          <DaemonPopoverContent error={data.error} type={data.type} />
        </Popover.Content>
      </Popover.Root>
    </ErrorBoundary>
  );
};

const OverallStatusBadge = (props: {
  machine: IAdminMachine;
  r2f: IReadyMsg | undefined;
}) => {
  const { machine, r2f } = props;

  if (!machine.connected) {
    // machine is d/c so we don't know anything about it
    return null;
  }

  if (!r2f?.machine_status) {
    // r2f has not arrived yet so we don't know anything about it
    return null;
  }

  return (
    <Badge color={getMachineStatusColor(r2f.machine_status)}>
      {getMachineStatusDisplay(r2f.machine_status)}
    </Badge>
  );
};

const BaseSection = (props: { header: string; content: ReactNode }) => (
  <Flex direction="column" gap="1" align="start">
    <Text size="2" color={RADIX.COLOR.SECONDARY}>
      {props.header}
    </Text>
    {props.content}
  </Flex>
);
export const MachineStatusSection = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const [r2f, setR2f] = useState<IReadyMsg>();

  const [netLog, setNetLog] = useState<IMachineNetworkLog>();

  // last-known ambient temperature; persists even if the machine stops
  // reporting a value (or disconnects) so we never blank out the reading.
  const [ambientTemp, setAmbientTemp] = useState<{
    celsius: number;
    at: string;
  }>();

  const errorDaemons = useMemo(
    () =>
      compact(
        Object.values(r2f?.full_state ?? {}).filter((m) => m?.error_present)
      ),
    [r2f]
  );

  const networkSpeed = useMemo(() => {
    const speed = netLog?.data?.speed;
    const down = speed?.download?.avg_mbps;
    const up = speed?.upload?.avg_mbps;

    if (typeof down !== 'number' || typeof up !== 'number') {
      return undefined;
    }

    const latencyLoss = netLog?.data?.latency_loss;
    const latencyMs = latencyLoss?.latency?.median_ms;
    const lossPct = latencyLoss?.packet_loss?.avg_pct;

    return {
      down,
      up,
      latencyMs: typeof latencyMs === 'number' ? latencyMs : undefined,
      lossPct: typeof lossPct === 'number' ? lossPct : undefined,
    };
  }, [netLog]);

  // fetch the latest network-speed log (periodic, not live over websocket)
  useEffect(() => {
    if (!machine?.machineID) {
      setNetLog(undefined);
      return;
    }

    AdminMachinesService.getInstance()
      .getLatestNetworkLog(machine.machineID)
      .then(setNetLog);
  }, [machine?.machineID]);

  // reset the last-known ambient temperature when switching machines so we
  // never show a stale reading belonging to a different machine.
  useEffect(() => {
    setAmbientTemp(undefined);
  }, [machine?.machineID]);

  // persist the latest valid ambient temperature from r2f
  useEffect(() => {
    const f = r2f?.full_state?.SystemMonitoring?.curr_ms_comps?.temp_ambient;

    if (typeof f === 'number' && Number.isFinite(f)) {
      setAmbientTemp({
        celsius: fahrenheitToCelsius(f),
        at: new Date().toISOString(),
      });
    }
  }, [r2f]);

  // listen to forwarded r2f messages
  useEffect(() => {
    const onMessage = (event: CustomEvent) => {
      const message: IWSMsg = event.detail;

      switch (message.type) {
        case WsMsgType.M2U_ReadyToFire: {
          setR2f(message.data as IReadyMsg);
          break;
        }

        default:
          console.debug(
            `Received unhandled message from forward: ${message.type}`
          );
          break;
      }
    };

    WebSocketHelper.on(WsMsgType.S2U_ForwardWs, onMessage);

    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_ForwardWs, onMessage);
    };
  }, []);

  if (!machine) {
    return null;
  }

  const connectionStatusDate = machine.connected
    ? machine.connect_date
    : machine.disconnect_date;

  const formattedConnectionStatusDate = connectionStatusDate
    ? formatDate(connectionStatusDate)
    : undefined;

  const formattedCalibrationStatusDate = formatDate(
    machine.last_calibrated_updated
  );

  const getGameStatusBadgeColor = () => {
    switch (machine.isInGame) {
      case true:
        return RADIX.COLOR.SUCCESS;

      case false:
      default:
        return RADIX.COLOR.DANGER;
    }
  };

  const speedMins = machine.enable_trajekt_vision
    ? NETWORK_SPEED_MIN_MBPS.vision
    : NETWORK_SPEED_MIN_MBPS.nonVision;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
      <Flex justify="between">
        <Heading size="4">{t('admin.status')}</Heading>
        <MachineSubscriptionButton type="details" />
      </Flex>
      <Card size="2">
        <Grid
          columns={{
            xs: '2',
            lg: '3',
          }}
          gap={RADIX.FLEX.GAP.MD}
        >
          {/* Connection Status */}
          <BaseSection
            header={t('common.machine-status')}
            content={
              <>
                <Flex gap={RADIX.FLEX.GAP.XS} align="center" justify="center">
                  <Badge
                    color={
                      machine.connected
                        ? RADIX.COLOR.SUCCESS
                        : RADIX.COLOR.DANGER
                    }
                  >
                    {machine.connected ? t('admin.online') : t('admin.offline')}
                  </Badge>

                  <OverallStatusBadge machine={machine} r2f={r2f} />
                </Flex>
                {formattedConnectionStatusDate && (
                  <Text size={RADIX.TEXT.SIZE.XS}>
                    {t('admin.last-connected')}: {formattedConnectionStatusDate}
                  </Text>
                )}
              </>
            }
          />

          {/* Calibration Status */}
          <BaseSection
            header={t('admin.last-realignment')}
            content={
              <>
                <Badge
                  color={
                    machine.calibrated
                      ? RADIX.COLOR.SUCCESS
                      : RADIX.COLOR.DANGER
                  }
                >
                  {machine.calibrated ? t('admin.passed') : t('admin.failed')}
                </Badge>
                {formattedCalibrationStatusDate && (
                  <Text size={RADIX.TEXT.SIZE.XS}>
                    {t('common.updated')}: {formattedCalibrationStatusDate}
                  </Text>
                )}
              </>
            }
          />

          {/* Game Status */}
          <BaseSection
            header={t('common.game-status')}
            content={
              <Badge color={getGameStatusBadgeColor()}>
                {machine.isInGame ? 'In Progress' : 'No Game'}
              </Badge>
            }
          />

          {ambientTemp !== undefined && (
            <BaseSection
              header="Ambient Temperature"
              content={
                <>
                  <Text>{ambientTemp.celsius.toFixed(1)} °C</Text>
                </>
              }
            />
          )}

          {networkSpeed && (
            <BaseSection
              header="Network Speed"
              content={
                <>
                  <Flex gap={RADIX.FLEX.GAP.XS} align="center" wrap="wrap">
                    <CommonTooltip
                      content={`Download ${networkSpeed.down.toFixed(
                        0
                      )} Mbps / Upload ${networkSpeed.up.toFixed(0)}`}
                      trigger={
                        <Badge
                          color={worstSpeedColor(
                            speedColor(networkSpeed.down, speedMins.download),
                            speedColor(networkSpeed.up, speedMins.upload)
                          )}
                        >
                          <CaretSortIcon /> {networkSpeed.down.toFixed(0)} /{' '}
                          {networkSpeed.up.toFixed(0)} Mbps
                        </Badge>
                      }
                    />

                    {networkSpeed.latencyMs !== undefined && (
                      <CommonTooltip
                        content="Median round-trip network latency"
                        trigger={
                          <Badge color={latencyColor(networkSpeed.latencyMs)}>
                            <LapTimerIcon /> {networkSpeed.latencyMs.toFixed(0)}{' '}
                            ms
                          </Badge>
                        }
                      />
                    )}

                    {networkSpeed.lossPct !== undefined && (
                      <CommonTooltip
                        content="Percentage of network packets lost over the sampling window"
                        trigger={
                          <Badge color={packetLossColor(networkSpeed.lossPct)}>
                            <StackIcon /> {networkSpeed.lossPct.toFixed(1)}%
                          </Badge>
                        }
                      />
                    )}
                  </Flex>
                  {netLog?._created && (
                    <Text size={RADIX.TEXT.SIZE.XS}>
                      {t('common.updated')}: {formatDate(netLog._created)}
                    </Text>
                  )}
                </>
              }
            />
          )}

          {errorDaemons.length > 0 && (
            <BaseSection
              header="Processes in Error"
              content={
                <>
                  {errorDaemons.map((m) => (
                    <DaemonBadge key={m.error_id ?? m.sender_id} daemon={m} />
                  ))}
                </>
              }
            />
          )}
        </Grid>
      </Card>
    </Flex>
  );
};
